import React, { useState, useEffect, useRef } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import UserLayout from '../../Layouts/UserLayout'
import useTranslation from '../../hooks/useTranslation'
// import { debounce } from 'lodash' // Removed lodash dependency

const TAG_COLORS = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-green-100 text-green-800 border-green-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-yellow-100 text-yellow-800 border-yellow-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-red-100 text-red-800 border-red-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-cyan-100 text-cyan-800 border-cyan-200'
]

const LANGUAGE_COLORS = {
  fr: 'bg-blue-100 text-blue-800 border-blue-200',
  es: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  de: 'bg-red-100 text-red-800 border-red-200',
  it: 'bg-green-100 text-green-800 border-green-200',
  pt: 'bg-orange-100 text-orange-800 border-orange-200',
  ru: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  ja: 'bg-pink-100 text-pink-800 border-pink-200',
  zh: 'bg-purple-100 text-purple-800 border-purple-200',
  en: 'bg-gray-100 text-gray-800 border-gray-200',
}

const LANGUAGE_LABELS = {
  fr: 'FR',
  es: 'ES',
  de: 'DE',
  it: 'IT',
  pt: 'PT',
  ru: 'RU',
  ja: 'JA',
  zh: 'ZH',
  en: 'EN',
}

export default function Index({ texts, filters, current_filters }) {
  const { t } = useTranslation()
  const { auth } = usePage().props
  const userLocale = auth?.user?.native_language || 'en'
  const [search, setSearch] = useState(current_filters.search || '')
  
  const getTagColor = (index) => {
    return TAG_COLORS[index % TAG_COLORS.length]
  }
  
  // Debounce search
  const searchRef = useRef(null)

  useEffect(() => {
    // skip first render
    if (searchRef.current === null) {
      searchRef.current = search
      return
    }
    
    // if search changed
    if (searchRef.current !== search) {
      const timer = setTimeout(() => {
        applyFilters({ search })
      }, 300)
      searchRef.current = search
      return () => clearTimeout(timer)
    }
  }, [search])

  const applyFilters = (newFilters) => {
    router.get('/texts', {
      ...current_filters,
      ...newFilters
    }, {
      preserveState: true,
      preserveScroll: true,
      replace: true
    })
  }

  return (
    <UserLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h1 className="text-3xl font-bold">{t('library.title')}</h1>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {/* Search */}
            <input 
              type="text" 
              placeholder="Search..." 
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-full sm:w-64"
            />
            
            {/* Filters */}
            <select 
              value={current_filters.language || ''}
              onChange={e => applyFilters({ language: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All Languages</option>
              {filters.languages.map(lang => (
                <option key={lang} value={lang}>{LANGUAGE_LABELS[lang] || lang.toUpperCase()}</option>
              ))}
            </select>

            <select 
              value={current_filters.tag_id || ''}
              onChange={e => applyFilters({ tag_id: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All Topics</option>
              {filters.tags?.map(tag => (
                <option key={tag.id} value={tag.id}>{tag.name}</option>
              ))}
            </select>

            <select 
              value={current_filters.level || ''}
              onChange={e => applyFilters({ level: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All Levels</option>
              {filters.levels.map(level => (
                <option key={level} value={level}>Level {level}</option>
              ))}
            </select>
            
            {(current_filters.search || current_filters.language || current_filters.tag_id || current_filters.level) && (
              <button 
                onClick={() => {
                  setSearch('')
                  router.get('/texts')
                }}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        
        {texts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
            <p className="text-gray-500 text-lg">No texts found matching your criteria.</p>
            <button 
              onClick={() => {
                setSearch('')
                router.get('/texts')
              }}
              className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {texts.map(text => (
              <Link 
                href={`/texts/${text.id}`} 
                key={text.id}
                className="group block bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-200 overflow-hidden flex flex-col h-full"
              >
                <div className="h-48 w-full overflow-hidden bg-gray-100 relative">
                  {/* Language Badge */}
                  <div className={`absolute top-3 right-3 z-10 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider shadow-sm border ${LANGUAGE_COLORS[text.language] || 'bg-gray-100 text-gray-800'}`}>
                    {text.language || '??'}
                  </div>

                  {text.image_url ? (
                     <img 
                       src={text.image_url} 
                       alt={text.title} 
                       className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                       onError={(e) => {
                         e.target.onerror = null
                         e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(text.title)}&background=random&size=512`
                       }}
                     />
                  ) : (
                     <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 text-emerald-200">
                       <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                       </svg>
                     </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">{text.title}</h2>
                  <div className="mt-auto pt-4 flex flex-col gap-2 text-sm border-t border-gray-50">
                    <div className="flex flex-wrap gap-1.5">
                      {text.tags && text.tags.length > 0 ? (
                        text.tags.map((tag, index) => {
                          const tagName = tag[`name_${userLocale}`] || tag.name_en || tag.name
                          return (
                            <span
                              key={tag.id}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getTagColor(index)}`}
                            >
                              {tagName}
                            </span>
                          )
                        })
                      ) : (
                        <span className="text-gray-400 italic text-xs">No topics</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-xs">
                        {/* Additional info can go here */}
                      </span>
                      <span className="flex items-center gap-1 font-medium bg-gray-100 px-2 py-0.5 rounded-full text-gray-600 text-xs">
                        Lvl {text.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </UserLayout>
  )
}
